<script setup>
import { ref, computed } from 'vue'
import { useStorage } from '@/core/composables/useStorage'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  folder: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    default: '',
  },
  accept: {
    type: String,
    default: 'image/*',
  },
})

const emit = defineEmits(['update:modelValue'])

const { uploadImage, deleteImageByUrl, uploading, progress, error } = useStorage()
const fileInput = ref(null)

const hasImage = computed(() => !!props.modelValue)

function triggerUpload() {
  fileInput.value?.click()
}

async function handleFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    emit('error', 'סוג קובץ לא נתמך. יש להעלות תמונה בלבד.')
    return
  }

  try {
    const url = await uploadImage(file, props.folder, props.fileName)
    emit('update:modelValue', url)
  } catch (err) {
    console.error('Upload failed:', err)
  }

  // Reset input
  e.target.value = ''
}

async function handleDelete() {
  if (!props.modelValue) return

  try {
    await deleteImageByUrl(props.modelValue)
    emit('update:modelValue', '')
  } catch (err) {
    console.error('Delete failed:', err)
  }
}
</script>

<template>
  <div class="image-upload" :class="{ 'has-image': hasImage, uploading }">
    <p v-if="label" class="upload-label">{{ label }}</p>

    <div class="upload-area" @click="!hasImage && !uploading && triggerUpload()">
      <!-- Empty state -->
      <div v-if="!hasImage && !uploading" class="empty-state">
        <span class="material-symbols-rounded upload-icon">add_a_photo</span>
        <span class="upload-text">לחץ להעלאת תמונה</span>
      </div>

      <!-- Uploading state -->
      <div v-if="uploading" class="uploading-state">
        <div class="progress-ring">
          <svg viewBox="0 0 36 36">
            <path
              class="progress-bg"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              class="progress-bar"
              :stroke-dasharray="`${progress}, 100`"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span class="progress-text">{{ Math.round(progress) }}%</span>
        </div>
      </div>

      <!-- Image preview -->
      <div v-if="hasImage && !uploading" class="image-preview">
        <img :src="modelValue" alt="תמונה שהועלתה" />
        <button type="button" class="delete-btn" @click.stop="handleDelete">
          <span class="material-symbols-rounded">delete</span>
        </button>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      class="hidden-input"
      @change="handleFileChange"
    />

    <p v-if="error" class="error-text">{{ error }}</p>
  </div>
</template>

<style scoped>
.image-upload {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.upload-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin: 0;
}

.upload-area {
  width: 100%;
  aspect-ratio: 4 / 3;
  max-height: 200px;
  border: 2px dashed var(--border-color);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  overflow: hidden;
  background: var(--bg-secondary);
}

.upload-area:hover:not(.has-image):not(.uploading) {
  border-color: var(--color-primary);
  background: rgba(99, 102, 241, 0.05);
}

.has-image .upload-area {
  cursor: default;
  border-style: solid;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--text-muted);
}

.upload-icon {
  font-size: 40px;
}

.upload-text {
  font-size: var(--font-size-sm);
}

.uploading-state {
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-ring {
  position: relative;
  width: 60px;
  height: 60px;
}

.progress-ring svg {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.progress-bg {
  fill: none;
  stroke: var(--border-color);
  stroke-width: 3;
}

.progress-bar {
  fill: none;
  stroke: var(--color-primary);
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray var(--transition-fast);
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
}

.image-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-btn {
  position: absolute;
  bottom: var(--spacing-sm);
  left: var(--spacing-sm);
  width: 36px;
  height: 36px;
  border-radius: var(--border-radius-sm);
  background: var(--color-error);
  color: var(--color-white);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  box-shadow: var(--shadow-md);
}

.delete-btn:hover {
  background: #dc2626;
  transform: scale(1.05);
}

.delete-btn .material-symbols-rounded {
  font-size: 20px;
}

.hidden-input {
  display: none;
}

.error-text {
  font-size: var(--font-size-xs);
  color: var(--color-error);
  margin: 0;
}
</style>
